/* ==========================================================================
   SABLE POCHETTE - Animations V3
   Sand falling + Parallax scroll + Horizontal slide on scroll
   ========================================================================== */

(function() {
  'use strict';

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    initLoader();
    initSandCanvas();
    initParallaxScroll();
    initHorizontalSlide();
    initScrollReveal();
    initSmoothScroll();
    initFAQ();
  }

  /* --------------------------------------------------------------------------
     Loader
     -------------------------------------------------------------------------- */
  function initLoader() {
    const loader = document.getElementById('sable-loader');
    if (!loader) return;

    window.addEventListener('load', () => {
      setTimeout(() => loader.classList.add('loaded'), 500);
    });

    // Fallback
    setTimeout(() => loader.classList.add('loaded'), 3000);
  }

  /* --------------------------------------------------------------------------
     Sand Falling Canvas Animation
     Particules de sable dore qui tombent
     -------------------------------------------------------------------------- */
  function initSandCanvas() {
    const canvas = document.getElementById('sand-canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let animationId;
    let isVisible = true;

    // Set canvas size
    function resize() {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    }

    resize();
    window.addEventListener('resize', resize);

    // Sand Particle Class
    class SandParticle {
      constructor() {
        this.reset();
      }

      reset() {
        // Random position across canvas width
        this.x = Math.random() * canvas.offsetWidth;
        this.y = -10 - Math.random() * 100;

        // Size variation
        this.size = Math.random() * 4 + 1;

        // Speed variation
        this.speedY = Math.random() * 1.5 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.3;

        // Slight wobble
        this.wobbleSpeed = Math.random() * 0.02 + 0.01;
        this.wobbleAngle = Math.random() * Math.PI * 2;

        // Opacity
        this.opacity = Math.random() * 0.5 + 0.3;

        // Gold/Sand color variations
        const colors = [
          '#C4B089', // Brand sand
          '#E8DCC4', // Light sand
          '#A89A6F', // Dark sand
          '#D4C4A8', // Cream sand
          '#F5E6C8', // Bright sand
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        // Falling motion
        this.y += this.speedY;

        // Gentle wobble
        this.wobbleAngle += this.wobbleSpeed;
        this.x += Math.sin(this.wobbleAngle) * 0.3 + this.speedX;

        // Reset when below canvas
        if (this.y > canvas.offsetHeight + 20) {
          this.reset();
        }

        // Wrap horizontally
        if (this.x < -10) this.x = canvas.offsetWidth + 10;
        if (this.x > canvas.offsetWidth + 10) this.x = -10;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // Create particles
    const particleCount = Math.min(80, Math.floor(canvas.offsetWidth / 15));
    for (let i = 0; i < particleCount; i++) {
      const p = new SandParticle();
      // Distribute initially across canvas
      p.y = Math.random() * canvas.offsetHeight;
      particles.push(p);
    }

    // Animation loop
    function animate() {
      if (!isVisible) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      particles.forEach(p => {
        p.update();
        p.draw();
      });

      animationId = requestAnimationFrame(animate);
    }

    // Visibility observer for performance
    const observer = new IntersectionObserver((entries) => {
      isVisible = entries[0].isIntersecting;
    }, { threshold: 0.1 });

    observer.observe(canvas);

    animate();
  }

  /* --------------------------------------------------------------------------
     Parallax Scroll Effect
     Elements move at different speeds when scrolling
     -------------------------------------------------------------------------- */
  function initParallaxScroll() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    let ticking = false;

    function updateParallax() {
      const scrollY = window.pageYOffset;

      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.5;
        const rect = el.getBoundingClientRect();
        const elementTop = rect.top + scrollY;
        const elementCenter = elementTop + rect.height / 2;
        const viewportCenter = scrollY + window.innerHeight / 2;
        const distance = viewportCenter - elementCenter;

        // Apply transform
        const yOffset = distance * speed;
        el.style.transform = `translate3d(0, ${yOffset}px, 0)`;
      });

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });

    // Initial call
    updateParallax();
  }

  /* --------------------------------------------------------------------------
     Horizontal Slide on Scroll
     Elements slide from right to left when scrolling
     -------------------------------------------------------------------------- */
  function initHorizontalSlide() {
    const slideElements = document.querySelectorAll('[data-slide]');
    if (!slideElements.length) return;

    let ticking = false;

    function updateSlide() {
      const scrollY = window.pageYOffset;
      const windowHeight = window.innerHeight;

      slideElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const elementTop = rect.top;

        // Calculate progress (0 to 1) as element enters viewport
        const progress = Math.max(0, Math.min(1, 1 - (elementTop / windowHeight)));

        // Get slide direction and distance
        const direction = el.dataset.slide || 'left';
        const distance = parseFloat(el.dataset.slideDistance) || 100;

        let translateX = 0;
        if (direction === 'left') {
          translateX = distance * (1 - progress);
        } else if (direction === 'right') {
          translateX = -distance * (1 - progress);
        }

        // Apply transform with easing
        el.style.transform = `translate3d(${translateX}px, 0, 0)`;
        el.style.opacity = progress;
      });

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateSlide);
        ticking = true;
      }
    }, { passive: true });

    // Initial call
    updateSlide();
  }

  /* --------------------------------------------------------------------------
     Scroll Reveal Animations
     Elements fade in and animate when entering viewport
     -------------------------------------------------------------------------- */
  function initScrollReveal() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    if (!animatedElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = parseInt(el.dataset.delay) || 0;

          setTimeout(() => {
            el.classList.add('animated');
          }, delay);

          observer.unobserve(el);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });

    animatedElements.forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------------------------------
     Smooth Scroll for Anchor Links
     -------------------------------------------------------------------------- */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;

        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          const headerOffset = 80;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     FAQ Accordion
     -------------------------------------------------------------------------- */
  function initFAQ() {
    const items = document.querySelectorAll('.sable-faq-item');

    items.forEach(item => {
      const question = item.querySelector('.sable-faq-item__question');
      if (!question) return;

      question.addEventListener('click', () => {
        const wasActive = item.classList.contains('active');

        // Close all others
        items.forEach(i => i.classList.remove('active'));

        // Toggle current
        if (!wasActive) {
          item.classList.add('active');
        }
      });
    });
  }

})();

/* --------------------------------------------------------------------------
   Additional Scroll-based Animations (GSAP-like without GSAP)
   Horizontal scrolling effect for images
   -------------------------------------------------------------------------- */
(function() {
  'use strict';

  document.addEventListener('DOMContentLoaded', function() {
    // Create horizontal scroll gallery effect
    const galleries = document.querySelectorAll('.sable-scroll-gallery');

    galleries.forEach(gallery => {
      const items = gallery.querySelectorAll('.sable-scroll-gallery__item');
      let ticking = false;

      function updateGallery() {
        const rect = gallery.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const progress = 1 - (rect.top / viewportHeight);
        const clampedProgress = Math.max(0, Math.min(1, progress));

        // Move items horizontally based on scroll
        items.forEach((item, index) => {
          const offset = (index - items.length / 2) * 50;
          const translateX = offset * (1 - clampedProgress);
          item.style.transform = `translateX(${translateX}px)`;
        });

        ticking = false;
      }

      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(updateGallery);
          ticking = true;
        }
      }, { passive: true });
    });

    // Stagger animation for grid items
    const grids = document.querySelectorAll('[data-stagger]');

    grids.forEach(grid => {
      const items = grid.children;

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          Array.from(items).forEach((item, index) => {
            setTimeout(() => {
              item.classList.add('animated');
            }, index * 100);
          });
          observer.unobserve(grid);
        }
      }, { threshold: 0.2 });

      observer.observe(grid);
    });
  });
})();
